import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, TestTube, ArrowLeft, AlertTriangle, Truck } from "lucide-react";
import { toast } from "sonner";
import { fetchProduct, formatGBP, resolveImageUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSEO } from "../hooks/useSEO";

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    fetchProduct(id)
      .then(setProduct)
      .catch(() => setError("Product not found."));
  }, [id]);

  useSEO(
    product
      ? {
          title: `${product.name} ${product.dosage} · Research peptide`,
          description: `${product.name} ${product.dosage} — ${product.short_description} Purity ${product.purity}. Free UK shipping from Sterling Bio. For laboratory research only — not for human use.`,
          path: `/products/${id}`,
          image: resolveImageUrl(product.image_url) || undefined,
          type: "product",
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Product",
            name: `${product.name} ${product.dosage}`,
            sku: product.sku,
            description: product.description,
            brand: { "@type": "Brand", name: "Sterling Bio" },
            image: resolveImageUrl(product.image_url) || undefined,
            offers: {
              "@type": "Offer",
              priceCurrency: "GBP",
              price: product.price_gbp,
              availability:
                product.stock > 0
                  ? "https://schema.org/InStock"
                  : "https://schema.org/OutOfStock",
              shippingDetails: {
                "@type": "OfferShippingDetails",
                shippingRate: { "@type": "MonetaryAmount", value: 0, currency: "GBP" },
                shippingDestination: { "@type": "DefinedRegion", addressCountry: "GB" },
              },
              url: typeof window !== "undefined" ? window.location.href : undefined,
            },
            ...(product.cas_number ? { identifier: { "@type": "PropertyValue", name: "CAS", value: product.cas_number } } : {}),
          },
        }
      : { title: "Product · Research peptide", path: `/products/${id}` },
  );

  if (error)
    return (
      <div data-testid="product-error" className="max-w-7xl mx-auto px-6 py-32 text-center text-[#64748B]">
        {error} · <Link to="/products" className="text-[#2C8C9C]">Back to catalogue</Link>
      </div>
    );

  if (!product)
    return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-[#64748B]">Loading…</div>;

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/products" className="inline-flex items-center gap-2 text-sm text-[#475569] sb-link-underline">
        <ArrowLeft className="w-4 h-4" /> Back to catalogue
      </Link>

      <div className="mt-8 grid lg:grid-cols-2 gap-12">
        <div className="border border-[#E2E8F0] bg-[#F8FAFC] sb-grid-overlay aspect-square flex items-center justify-center relative overflow-hidden">
          {resolveImageUrl(product.image_url) ? (
            <img
              src={resolveImageUrl(product.image_url)}
              alt={`${product.name} ${product.dosage}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <TestTube className="w-40 h-40 text-[#2C8C9C]/40" strokeWidth={1.25} />
          )}
          <div className="absolute top-4 left-4 sb-mono-label bg-white/90 px-1.5 py-0.5">{product.sku}</div>
          <div className="absolute bottom-4 left-4 right-4 bg-white border border-[#E2E8F0] p-3 flex items-center gap-2 text-xs">
            <AlertTriangle className="w-4 h-4 text-[#B91C1C] shrink-0" />
            <span className="text-[#B91C1C] font-mono uppercase tracking-wider">For research use only — not for human consumption</span>
          </div>
        </div>

        <div>
          <div className="sb-mono-label">{product.category} · {product.dosage}</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-semibold text-[#1B3A5C] tracking-tight" style={{ fontFamily: "Outfit" }}>
            {product.name}
          </h1>

          <div className="mt-4 flex items-baseline gap-4">
            <div className="text-3xl font-semibold text-[#1B3A5C]" style={{ fontFamily: "Outfit" }}>
              {formatGBP(product.price_gbp)}
            </div>
            <div data-testid="detail-stock" className="font-mono text-sm text-[#475569]">
              · {product.stock} in stock
            </div>
          </div>

          <p className="mt-6 text-[#475569] leading-relaxed">{product.description}</p>

          <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-b border-[#E2E8F0] py-6">
            <div>
              <dt className="sb-mono-label">SKU</dt>
              <dd className="font-mono text-sm text-[#0F172A] mt-1">{product.sku}</dd>
            </div>
            <div>
              <dt className="sb-mono-label">Purity</dt>
              <dd className="font-mono text-sm text-[#0F172A] mt-1">{product.purity}</dd>
            </div>
            {product.cas_number && (
              <div>
                <dt className="sb-mono-label">CAS</dt>
                <dd className="font-mono text-sm text-[#0F172A] mt-1">{product.cas_number}</dd>
              </div>
            )}
            {product.molecular_weight && (
              <div>
                <dt className="sb-mono-label">Mol. weight</dt>
                <dd className="font-mono text-sm text-[#0F172A] mt-1">{product.molecular_weight}</dd>
              </div>
            )}
            <div>
              <dt className="sb-mono-label">Form</dt>
              <dd className="font-mono text-sm text-[#0F172A] mt-1">Lyophilised</dd>
            </div>
            <div>
              <dt className="sb-mono-label">Origin</dt>
              <dd className="font-mono text-sm text-[#0F172A] mt-1">United Kingdom</dd>
            </div>
          </dl>

          <div className="mt-8 flex flex-wrap gap-4 items-center">
            <div className="inline-flex items-center border border-[#E2E8F0]">
              <button
                data-testid="qty-minus"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-10 h-12 flex items-center justify-center hover:bg-[#F1F5F9]"
                aria-label="Decrease quantity"
              >
                <Minus className="w-4 h-4" />
              </button>
              <input
                data-testid="qty-input"
                type="number"
                min={1}
                max={product.stock}
                value={qty}
                onChange={(e) => setQty(Math.max(1, Math.min(product.stock, Number(e.target.value) || 1)))}
                className="w-14 h-12 text-center font-mono border-0 focus:outline-none"
              />
              <button
                data-testid="qty-plus"
                onClick={() => setQty((q) => Math.min(product.stock, q + 1))}
                className="w-10 h-12 flex items-center justify-center hover:bg-[#F1F5F9]"
                aria-label="Increase quantity"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              data-testid="detail-add-cart"
              disabled={product.stock <= 0}
              onClick={handleAdd}
              className="bg-[#1B3A5C] text-white px-8 py-3 text-sm font-medium tracking-wide hover:bg-[#254D7A] transition-colors disabled:opacity-40"
            >
              {product.stock <= 0 ? "Out of stock" : "Add to cart"}
            </button>
          </div>

          <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#047857]">
            <Truck className="w-4 h-4" strokeWidth={1.75} />
            <span><strong>Free UK shipping</strong> · Same-day dispatch before 2pm</span>
          </div>

          <div className="mt-8 p-4 bg-[#FEF2F2] border border-[#B91C1C]/20">
            <div className="sb-warning-tag">Strict compliance notice</div>
            <p className="mt-2 text-sm text-[#7f1d1d] leading-relaxed">
              By purchasing, you confirm you are a qualified researcher and accept that this
              product will be used solely for in-vitro laboratory research. Sterling Bio
              accepts no liability for misuse.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
